import { AgeTarget, Canton, HouseholdSizeTarget, IncomeTarget } from '@/services/ws-post/src/main/resources/rest-api';

import { Step } from 'src/modules/PostCampaign/index.ts';
import type { PostDigitalAdChannel } from './types.ts';

export const AGE_NUMERIC = {
  [AgeTarget.ZERO]: 0,
  [AgeTarget.FIVE]: 5,
  [AgeTarget.TEN]: 10,
  [AgeTarget.FIFTEEN]: 15,
  [AgeTarget.EIGHTEEN]: 18,
  [AgeTarget.TWENTY]: 20,
  [AgeTarget.TWENTY_FIVE]: 25,
  [AgeTarget.THIRTY]: 30,
  [AgeTarget.THIRTY_FIVE]: 35,
  [AgeTarget.FORTY]: 40,
  [AgeTarget.FORTY_FIVE]: 45,
  [AgeTarget.FIFTY]: 50,
  [AgeTarget.FIFTY_FIVE]: 55,
  [AgeTarget.SIXTY]: 60,
  [AgeTarget.SIXTY_FIVE]: 65,
  [AgeTarget.SEVENTY]: 70,
  [AgeTarget.SEVENTY_FIVE]: 75,
  [AgeTarget.EIGHTY]: 80,
  [AgeTarget.EIGHTY_FIVE]: 85,
  [AgeTarget.NINETY]: 90,
  [AgeTarget.NINETY_PLUS]: 95,
};

/** Path API bucket order: one `AgeTarget` per bracket (indices 0..N-1 on the slider). */
export const PATH_AGE_BUCKETS = [
  AgeTarget.ZERO,
  AgeTarget.EIGHTEEN,
  AgeTarget.TWENTY_FIVE,
  AgeTarget.THIRTY,
  AgeTarget.THIRTY_FIVE,
  AgeTarget.FORTY,
  AgeTarget.FORTY_FIVE,
  AgeTarget.FIFTY,
  AgeTarget.FIFTY_FIVE,
  AgeTarget.SIXTY,
  AgeTarget.SIXTY_FIVE,
  AgeTarget.SEVENTY,
  AgeTarget.SEVENTY_FIVE,
  AgeTarget.EIGHTY,
  AgeTarget.EIGHTY_FIVE,
  AgeTarget.NINETY_PLUS,
] as const;

export type PathAgeBucket = (typeof PATH_AGE_BUCKETS)[number];

export const AGE_BUCKET_STEP: Record<PathAgeBucket, number> = Object.fromEntries(
  PATH_AGE_BUCKETS.map((target, index) => [target, index]),
) as Record<PathAgeBucket, number>;

export const AGE_BUCKET_INPUT_STEP: Record<AgeTarget, number> = {
  [AgeTarget.ZERO]: AGE_BUCKET_STEP[AgeTarget.ZERO],
  [AgeTarget.FIVE]: AGE_BUCKET_STEP[AgeTarget.ZERO],
  [AgeTarget.TEN]: AGE_BUCKET_STEP[AgeTarget.ZERO],
  [AgeTarget.FIFTEEN]: AGE_BUCKET_STEP[AgeTarget.ZERO],
  [AgeTarget.EIGHTEEN]: AGE_BUCKET_STEP[AgeTarget.EIGHTEEN],
  [AgeTarget.TWENTY]: AGE_BUCKET_STEP[AgeTarget.EIGHTEEN],
  [AgeTarget.TWENTY_FIVE]: AGE_BUCKET_STEP[AgeTarget.TWENTY_FIVE],
  [AgeTarget.THIRTY]: AGE_BUCKET_STEP[AgeTarget.THIRTY],
  [AgeTarget.THIRTY_FIVE]: AGE_BUCKET_STEP[AgeTarget.THIRTY_FIVE],
  [AgeTarget.FORTY]: AGE_BUCKET_STEP[AgeTarget.FORTY],
  [AgeTarget.FORTY_FIVE]: AGE_BUCKET_STEP[AgeTarget.FORTY_FIVE],
  [AgeTarget.FIFTY]: AGE_BUCKET_STEP[AgeTarget.FIFTY],
  [AgeTarget.FIFTY_FIVE]: AGE_BUCKET_STEP[AgeTarget.FIFTY_FIVE],
  [AgeTarget.SIXTY]: AGE_BUCKET_STEP[AgeTarget.SIXTY],
  [AgeTarget.SIXTY_FIVE]: AGE_BUCKET_STEP[AgeTarget.SIXTY_FIVE],
  [AgeTarget.SEVENTY]: AGE_BUCKET_STEP[AgeTarget.SEVENTY],
  [AgeTarget.SEVENTY_FIVE]: AGE_BUCKET_STEP[AgeTarget.SEVENTY_FIVE],
  [AgeTarget.EIGHTY]: AGE_BUCKET_STEP[AgeTarget.EIGHTY],
  [AgeTarget.EIGHTY_FIVE]: AGE_BUCKET_STEP[AgeTarget.EIGHTY_FIVE],
  [AgeTarget.NINETY]: AGE_BUCKET_STEP[AgeTarget.NINETY_PLUS],
  [AgeTarget.NINETY_PLUS]: AGE_BUCKET_STEP[AgeTarget.NINETY_PLUS],
};

export const AGE_BUCKET_DISPLAY: Record<PathAgeBucket, number> = {
  [AgeTarget.ZERO]: 0,
  [AgeTarget.EIGHTEEN]: 18,
  [AgeTarget.TWENTY_FIVE]: 25,
  [AgeTarget.THIRTY]: 30,
  [AgeTarget.THIRTY_FIVE]: 35,
  [AgeTarget.FORTY]: 40,
  [AgeTarget.FORTY_FIVE]: 45,
  [AgeTarget.FIFTY]: 50,
  [AgeTarget.FIFTY_FIVE]: 55,
  [AgeTarget.SIXTY]: 60,
  [AgeTarget.SIXTY_FIVE]: 65,
  [AgeTarget.SEVENTY]: 70,
  [AgeTarget.SEVENTY_FIVE]: 75,
  [AgeTarget.EIGHTY]: 80,
  [AgeTarget.EIGHTY_FIVE]: 85,
  [AgeTarget.NINETY_PLUS]: 90,
};

export const AGE_BUCKET_MARKS = PATH_AGE_BUCKETS.map((_, index) => index);

export const LEGACY_AGE_ORDER: AgeTarget[] = (Object.entries(AGE_NUMERIC) as [AgeTarget, number][])
  .sort(([, left], [, right]) => left - right)
  .map(([key]) => key);

export const getAgeOverviewNumericMap = (): Record<AgeTarget, number> => ({ ...AGE_NUMERIC, ...AGE_BUCKET_DISPLAY });

export const INCOME_NUMERIC: Record<IncomeTarget, number> = {
  [IncomeTarget.ZERO]: 0,
  [IncomeTarget.TWENTY_FIVE]: 25,
  [IncomeTarget.FORTY]: 40,
  [IncomeTarget.SIXTY]: 60,
  [IncomeTarget.EIGHTY]: 80,
  [IncomeTarget.ONE_HUNDRED]: 100,
  [IncomeTarget.ONE_HUNDRED_FIFTY]: 150,
  [IncomeTarget.THREE_HUNDRED]: 300,
  [IncomeTarget.TREE_HUNDRED_PLUS]: 350,
};

export const HOUSEHOLD_SIZE_NUMERIC = {
  [HouseholdSizeTarget.ONE]: 1,
  [HouseholdSizeTarget.TWO]: 2,
  [HouseholdSizeTarget.THREE]: 3,
  [HouseholdSizeTarget.FOUR]: 4,
  [HouseholdSizeTarget.FIVE]: 5,
  [HouseholdSizeTarget.SIX]: 6,
};

export const RADIUS_MAX = 175;
export const DRIVING_MINUTES_MIN = 1;
export const DRIVING_MINUTES_MAX = 60;

// `DigitalAdChannel` is a nullable enum, so the generator emits it as a union-type alias (not an enum
// object) — hence bare string literals here rather than enum members. The `PostDigitalAdChannel[]`
// annotation (= `DigitalAdChannel` without `null`) keeps these type-checked against the union.
export const DIGITAL_AD_CHANNELS: PostDigitalAdChannel[] = [
  'SCREENS_PUBLIC_TRANSPORT',
  'SCREENS_CITY',
  'SCREENS_RETAIL',
  'SCREENS_IN_STORE',
  'SCREENS_GAS_STATION',
];

export const DEFAULT_POST_CODE_EXTENSION = '00';

export const CANTON_ITEMS = Object.values(Canton);

const basePath = '/orders/post-campaigns/:status/:campaignId';

export const STEP_PATH = {
  edit: `${basePath}/edit`,
  information: `${basePath}/information`,
  targetGroupSettings: `${basePath}/target-group-settings/:targetGroupId?`,
  calculation: `${basePath}/calculation`,
  editPositions: `${basePath}/edit-positions/:positionType?`,
  documentPreview: `${basePath}/document-preview`,
} as const;

export const REGULAR_CELL_PADDING = '8px';
export const HEADER_CELL_PADDING = '16px 8px';
export const LOADER_CELL_PADDING = '9px 8px';

export const PLACEHOLDER = '—';

export const STEPS: Step[] = [
  Step.GENERAL,
  Step.INFORMATION,
  Step.TARGET_GROUP_SETTINGS,
  Step.CALCULATION,
  Step.CONFIGURE_POSITIONS,
  Step.PREVIEW,
];

export const MIN_WEIGHT = 1;
export const MAX_STANDARD_MAILING_WEIGHT = 100;
export const MAX_REDUCED_STANDARD_MAILING_WEIGHT = 50;
export const MIN_MEDIUM_MAILING_WEIGHT = MAX_STANDARD_MAILING_WEIGHT + 1;
export const MAX_MEDIUM_MAILING_WEIGHT = 500;
export const MAX_LARGE_MAILING_WEIGHT = 1000;
export const ADDRESSED_MAILING_LETTER_PRODUCT_CHANGES_START_DATE_PRODUCTION = '2027-01-01';
export const ADDRESSED_MAILING_LETTER_PRODUCT_CHANGES_START_DATE_DEV = '2026-08-01';
